import PropTypes from 'prop-types';
import {useEffect, useRef, useState} from 'react';
import {useTheme} from 'react-jss';
import {connect} from 'react-redux';
import useHandleClickOutside from '../../hooks/useHandleClickOutside';
import DefineFieldPropType from '../../propTypes/DefineField.proptype';
import modulo from '../../utils/modulo';
import {showOverlay} from '../../v2/actions/overlay-actions';
import {getPudoCurrentLocation} from '../../v2/actions/pudo-actions';
import {CurrentLocation as CollectionCurrentLocation} from '../../v2/components/Collection/CollectionPoint/CurrentLocation';
import {TooltipAndOverlayIDs} from '../../v2/components/detailed-tooltip/TooltipLibrary';
import CurrentLocation from '../../v2/components/pudo/PudoCollectionPoint/CurrentLocation';
import MagnifyingGlass from '../../v2/components/ui/svg/Components/MagnifyingGlassIcon';
import Translation from '../../v2/lang/Translation';
import {isMobile} from '../../v2/selectors/DeviceSelectors';
import Button from '../Button/Button';
import useStyles from './Autocomplete.styles';
import AutocompleteResult from './AutocompleteResult/AutocompleteResult';
import AutocompleteTextResult from './AutocompleteTextResult/AutocompleteTextResult';

export function AutocompleteFieldWithBetterClickHandling(props) {
  const theme = useTheme();
  const classes = useStyles({...props, theme});
  
  const [expanded, setExpanded] = useState(false);
  const [activeResult, setActiveResult] = useState(-1);
  const [selected, setSelected] = useState(false);
  const didMount = useRef(false);
  const valueRef = useRef('');
  const inputRef = useRef('');
  const resultsWrapper = useRef('');
  const fallbackWrapper = useRef('');
  
  const resultsLength = props.predictions.length;
  const renderResults = expanded && resultsLength > 0;
  const renderFallback = expanded && resultsLength === 0;
  
  const hideResults = () => {
    setActiveResult(-1);
    setExpanded(false);
  };
  
  const showResults = (event) => {
    props.handleFocus(event);
    setActiveResult(-1);
    setExpanded(true);
  };
  
  const simulateBlur = (event) => {
    hideResults();
    props.onBlur(event);
  };
  
  const selectResult = (index) => {
    if (props.predictions[index]) {
      props.onSelect(props.predictions[index]);
      setSelected(true);
      
      const listItem = {
        target: {
          value: props.predictions[index],
          name: props.nameOnForm || '',
        },
      };
      
      if (props.id === 'InputField--loqate.LoqateAddressLookup') {
        inputRef.current.focus();
      } else {
        simulateBlur(listItem);
      }
    }
  };
  
  const selectActiveResult = () => {
    if (activeResult === -1) {
      selectResult(0);
    } else {
      selectResult(activeResult);
    }
  };
  
  const handleCurrentLocation = (event) => {
    setActiveResult(-1);
    setExpanded(false);
    props.getPudoCurrentLocation(props.isCollection, props.onClick);
    simulateBlur(event);
    if (props.shouldUseNewCollectionOverlay) {
      props.showOverlay(TooltipAndOverlayIDs.COLLECTION, null);
    }
  };
  
  const preventDefaults = (event) => {
    const stopPropagation = () => {
      event.preventDefault();
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation();
    };
    
    const keyHandlers = {
      ArrowUp: stopPropagation,
      ArrowDown: stopPropagation,
      Escape: stopPropagation,
      Enter: stopPropagation,
    };
    
    if (keyHandlers[event.key]) {
      keyHandlers[event.key]();
    }
  };
  
  function setActiveResultWithinBounds(desiredIndex) {
    setActiveResult(modulo(desiredIndex, resultsLength));
  }
  
  const moveDownResults = (event) => {
    if (expanded) {
      if (resultsLength > 1) {
        setActiveResultWithinBounds(activeResult + 1);
      } else {
        setActiveResult(0);
      }
    } else {
      showResults();
    }
    event.preventDefault();
  };
  
  const handleKeyDown = (event) => {
    const keyHandlers = {
      Escape: hideResults,
      Enter: selectActiveResult,
      
      ArrowUp() {
        setActiveResultWithinBounds(activeResult - 1);
        event.preventDefault();
      },
      
      ArrowDown() {
        moveDownResults(event);
      },
    };
    
    if (keyHandlers[event.key]) {
      preventDefaults(event);
      keyHandlers[event.key]();
    }
  };
  
  const handleOnChange = (event) => {
    setSelected(false);
    props.onChange(event);
  };
  
  const getSearchIcon = () => (
    props.type === 'search' && !props.isCollection && (
      <Button
        className={classes.searchIcon}
        link
        noResize
        id="searchIcon"
        icon={<MagnifyingGlass />}
        onClick={() => selectActiveResult()}
      />
    )
  );
  
  useEffect(() => {
    if (didMount.current) {
      if (!selected && valueRef.current !== props.value) {
        showResults();
      }
    } else {
      didMount.current = true;
    }
    
    if (valueRef.current !== props.value) {
      valueRef.current = props.value;
    }
  }, [props.predictions]);
  
  const listboxID = `${props.id}-listbox`;
  
  const PredictionComponent = props.predictionComponent;
  
  useHandleClickOutside(resultsWrapper, simulateBlur);
  useHandleClickOutside(fallbackWrapper, simulateBlur);
  
  const renderAutocompleteField = () => (
    <div className={classes.autocompleteField} data-test="AutocompleteField">
      <div className="AutocompleteField__comboboxWrapper" data-cs-mask>
        <div
          className="AutocompleteField__comboxbox"
          role="combobox"
          aria-expanded={renderResults}
          aria-owns={listboxID}
          aria-haspopup="listbox"
        >
          <input
            id={props.id}
            className={`AutocompleteField__input ${props.className}`}
            type={props.type ? props.type : 'text'}
            aria-autocomplete="list"
            aria-controls={renderResults ? listboxID : undefined}
            aria-activedescendant={renderResults ? `result-item-${activeResult}` : undefined}
            aria-hidden={props.fieldCollapsed}
            onKeyUp={preventDefaults}
            onKeyDown={handleKeyDown}
            onFocus={showResults}
            onChange={handleOnChange}
            onBlur={props.onBlur}
            value={props.value}
            placeholder={props.placeholder ? Translation.getText(props.placeholder) : ''}
            autoComplete={props.autoComplete}
            ref={inputRef}
            name={props.nameOnForm}
          />
          {getSearchIcon()}
        </div>
        {expanded && (renderResults || (props.showCurrentLocation && props.value === '')) && (
          <ul
            id={listboxID}
            className={classes.listBox}
            data-test="AutocompleteField__listbox"
            aria-labelledby={`${props.id}-label`}
            aria-live="polite"
            role="listbox"
            ref={resultsWrapper}
          >
            {!renderResults && props.value === '' && props.showCurrentLocation && (
              <AutocompleteResult
                index={0}
                active={activeResult === 0}
                onClick={event => handleCurrentLocation(event)}
              >
                {
                  props.isCollection
                    ? <CollectionCurrentLocation data-test="collectionCurrentLocation" />
                    : <CurrentLocation data-test="currentLocation" />
                }
              </AutocompleteResult>
            )}
            {props.predictions.map((result, index) => {
              const active = activeResult === index;
              return (
                <AutocompleteResult
                  index={index}
                  onClick={() => selectResult(index)}
                  active={active}
                >
                  <PredictionComponent
                    isCollection={props.isCollection}
                    prediction={result}
                    active={active}
                  />
                </AutocompleteResult>
              );
            })}
          </ul>
        )}
        {renderFallback && props.emptyPredictionsFallback && (
          <div className={classes.listBox} ref={fallbackWrapper} data-test="AutocompleteFieldWithBetterClickHandling__fallback">
            <div className={classes.fallbackWrapper} data-test="AutocompleteField-fallbackWrapper">
              {props.emptyPredictionsFallback}
            </div>
          </div>
        )}
      </div>
    </div>
  );
  
  if (props.isCollection && props.shouldUseNewCollectionOverlay && !props.isOverlay) {
    return (
      <div className={classes.newCollectionWrapper}>
        {renderAutocompleteField()}
        <Button
          id="CollectionNewCollectionPoint__boxSearchButton"
          primary
          className={classes.newBoxSearchButton}
          onClick={() => selectActiveResult()}
        >
          <Translation>collection.search.button.find.a.store</Translation>
        </Button>
      </div>
    );
  }
  
  if ((props.isCollection && props.shouldUseNewCollectionOverlay && props.isOverlay) || (props.isCollection && !props.shouldUseNewCollectionOverlay)) {
    return (
      <div className={classes.collectionWrapper}>
        {renderAutocompleteField()}
        <Button
          id="CollectionNewCollectionPoint__boxSearchButton"
          primary
          className={classes.boxSearchButton}
          onClick={() => selectActiveResult()}
        >
          <Translation>collection.search.button</Translation>
        </Button>
      </div>
    );
  }
  
  return renderAutocompleteField();
}

AutocompleteFieldWithBetterClickHandling.propTypes = {
  id: PropTypes.string.isRequired,
  isCollection: PropTypes.bool,
  placeholder: PropTypes.string,
  className: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onClick: PropTypes.func,
  onSelect: PropTypes.func.isRequired,
  predictions: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])),
  predictionComponent: PropTypes.func,
  value: PropTypes.string.isRequired,
  emptyPredictionsFallback: PropTypes.node,
  showCurrentLocation: PropTypes.bool,
  handleFocus: PropTypes.func,
  getPudoCurrentLocation: PropTypes.func,
  type: PropTypes.string,
  autoComplete: PropTypes.string,
  nameOnForm: PropTypes.string,
  definedField: PropTypes.shape(DefineFieldPropType).isRequired,
  shouldUseNewCollectionOverlay: PropTypes.bool.isRequired,
  isOverlay: PropTypes.bool,
  showOverlay: PropTypes.func.isRequired,
  isMobile: PropTypes.bool,
  fieldCollapsed: PropTypes.bool,
};

const mapStateToProps = state => ({
  isMobile: isMobile(state),
});

const mapDispatchToProps = dispatch => ({
  getPudoCurrentLocation: (isCollection, callback) => {
    dispatch(getPudoCurrentLocation(isCollection, callback));
  },
  showOverlay: (overlayID, overlayData) => {
    dispatch(showOverlay(overlayID, overlayData));
  },
});

AutocompleteFieldWithBetterClickHandling.defaultProps = {
  predictionComponent: AutocompleteTextResult,
  onChange: () => {
  },
  onBlur: () => {
  },
  className: '',
  predictions: [],
  emptyPredictionsFallback: null,
  fieldCollapsed: false,
};

export default connect(mapStateToProps, mapDispatchToProps)(AutocompleteFieldWithBetterClickHandling);
